import React from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueCreationData, ValidationError } from '../../../types/league';

interface StepProps {
  data: Partial<LeagueCreationData>;
  updateData: (data: Partial<LeagueCreationData>) => void;
  errors: ValidationError[];
  addError: (field: string, message: string) => void;
  clearErrors: () => void;
}

const PointSystemStep: React.FC<StepProps> = ({ data, updateData, errors, clearErrors }) => {
  const { t } = useTranslation();
  const handlePointSystemChange = (field: string, value: number | boolean | string) => {
    const currentPointSystem = data.pointSystem || {
      points_per_game_win: 1,
      points_per_series_win: 1,
      points_per_both: false,
      custom_formula: ''
    };

    updateData({
      pointSystem: {
        ...currentPointSystem,
        [field]: value
      }
    });

    // Clear errors when user makes changes
    if (errors.some(e => e.field.startsWith('pointSystem'))) {
      clearErrors();
    }
  };

  const getFieldError = (field: string) => {
    return errors.find(e => e.field === field)?.message;
  };

  const pointSystem = data.pointSystem || {
    points_per_game_win: 1,
    points_per_series_win: 1,
    points_per_both: false,
    custom_formula: ''
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('admin.createLeague.steps.pointSystemStep.title')}</h2>
        <p className="text-purple-200">
          {t('admin.createLeague.steps.pointSystemStep.subtitle')}
        </p>
      </div>

      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-6">{t('admin.createLeague.steps.pointSystemStep.configTitle')}</h3>

        <div className="mt-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="points_per_both"
              checked={pointSystem.points_per_both}
              onChange={(e) => handlePointSystemChange('points_per_both', e.target.checked)}
              className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500"
            />
            <label htmlFor="points_per_both" className="ml-2 text-purple-200">
              {t('admin.createLeague.steps.pointSystemStep.pointsPerBoth')}
            </label>
          </div>
          <p className="text-purple-300 text-sm mt-2 ml-6">
            {t('admin.createLeague.steps.pointSystemStep.pointsPerBothHelp')}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.pointSystemStep.pointsPerGameWin')} *
            </label>
            <input
              type="number"
              min="0"
              value={pointSystem.points_per_game_win}
              onChange={(e) => handlePointSystemChange('points_per_game_win', parseInt(e.target.value) || 0)}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {getFieldError('pointSystem.points_per_game_win') && (
              <p className="text-red-400 text-sm mt-1">{getFieldError('pointSystem.points_per_game_win')}</p>
            )}
            <p className="text-purple-300 text-sm mt-1">
              {t('admin.createLeague.steps.pointSystemStep.pointsPerGameWinHelp')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.pointSystemStep.pointsPerSeriesWin')} *
            </label>
            <input
              type="number"
              min="0"
              disabled={!pointSystem.points_per_both}
              value={pointSystem.points_per_series_win}
              onChange={(e) => handlePointSystemChange('points_per_series_win', parseInt(e.target.value) || 0)}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {getFieldError('pointSystem.points_per_series_win') && (
              <p className="text-red-400 text-sm mt-1">{getFieldError('pointSystem.points_per_series_win')}</p>
            )}
            <p className="text-purple-300 text-sm mt-1">
              {t('admin.createLeague.steps.pointSystemStep.pointsPerSeriesWinHelp')}
            </p>
          </div>
        </div>


        <div className="mt-6">
          <label className="block text-sm font-medium text-purple-200 mb-2">
            {t('admin.createLeague.steps.pointSystemStep.customFormula')}
          </label>
          <textarea
            rows={4}
            value={pointSystem.custom_formula || ''}
            onChange={(e) => handlePointSystemChange('custom_formula', e.target.value)}
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder={t('admin.createLeague.steps.pointSystemStep.customFormulaPlaceholder')}
          />
          <p className="text-purple-300 text-sm mt-2">
            {t('admin.createLeague.steps.pointSystemStep.customFormulaHelp')}
          </p>
        </div>
      </div>

      {/* Point System Examples */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.pointSystemStep.commonSystems.title')}</h3>
        <div className="grid md:grid-cols-2 gap-6 text-purple-200 text-sm">
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.pointSystemStep.commonSystems.traditional.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.traditional.gameWin')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.traditional.seriesWin')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.traditional.total')}</li>
              <li>• (3 games + 1 series)</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.pointSystemStep.commonSystems.highValue.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.highValue.gameWin')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.highValue.seriesWin')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.highValue.total')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.highValue.breakdown')}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.pointSystemStep.commonSystems.seriesFocus.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.seriesFocus.gameWin')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.seriesFocus.seriesWin')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.seriesFocus.focus')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.seriesFocus.reducesLuck')}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.pointSystemStep.commonSystems.balanced.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.balanced.gameWin')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.balanced.seriesWin')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.balanced.rewards')}</li>
              <li>• {t('admin.createLeague.steps.pointSystemStep.commonSystems.balanced.total')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-blue-500/20 border border-blue-400 rounded-lg p-4">
        <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.pointSystemStep.preview.title')}</h4>
        <div className="text-blue-200 text-sm">
          <p>{t('admin.createLeague.steps.pointSystemStep.preview.description')}</p>
          <ul className="mt-2 space-y-1">
            <li>• {t('admin.createLeague.steps.pointSystemStep.preview.perGameWin', { points: pointSystem.points_per_game_win })}</li>
            <li>• {t('admin.createLeague.steps.pointSystemStep.preview.perSeriesWin', { points: pointSystem.points_per_series_win })}</li>
          </ul>
          <p className="mt-3 text-blue-300 font-medium">
            {t('admin.createLeague.steps.pointSystemStep.preview.exampleTitle')}
          </p>
          <ul className="mt-1 space-y-1 ml-4">
            <li>• {t('admin.createLeague.steps.pointSystemStep.preview.maxPossible', { 
              points: pointSystem.points_per_both ? 
                (3 * pointSystem.points_per_game_win + pointSystem.points_per_series_win) : 
                Math.max(3 * pointSystem.points_per_game_win, pointSystem.points_per_series_win)
            })}</li>
          </ul>
          <p className="mt-2 text-xs text-blue-400">
            💡 {t('admin.createLeague.steps.pointSystemStep.preview.actualNote')}
          </p>
        </div>
      </div>
    </div>
  );
};

export const LeaderboardStep: React.FC<StepProps> = () => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-white mb-2">Leaderboard & Payouts</h2>
    <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4">
      <p className="text-yellow-200">🚧 Leaderboard and payout configuration coming soon.</p>
    </div>
  </div>
);

export const AwardsStep: React.FC<StepProps> = () => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-white mb-2">Awards & Prizes</h2>
    <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4">
      <p className="text-yellow-200">🚧 Awards configuration coming soon.</p>
    </div>
  </div>
);

export const SideEventsStep: React.FC<StepProps> = () => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-white mb-2">Side Events</h2>
    <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4">
      <p className="text-yellow-200">🚧 Side events configuration coming soon.</p>
    </div>
  </div>
);

export default PointSystemStep;